import { Random } from '@framework/Random';
import { Timestamp } from '@framework/Timestamp';
import { isEqual } from 'lodash-es';
import type { ModelCommentId, UserId, ModelCommentJSON } from 'balus-common';

export class ModelCommentEntity {
  constructor(
    public readonly id: ModelCommentId,
    public readonly userId: UserId,
    private readonly body: string,
    private readonly createdAt: Timestamp,
    private readonly updatedAt: Timestamp,
    private readonly type: ModelCommentJSON['type']
  ) {}

  static buildNewId(): ModelCommentId {
    return Random.generateRandomID(20, 'MC');
  }

  static buildNew(userId: UserId, body: string, type?: ModelCommentJSON['type']): ModelCommentEntity {
    const now = Timestamp.now();
    return new this(this.buildNewId(), userId, body, now, now, type || 'Regular');
  }

  static load(dump: ModelCommentJSON): ModelCommentEntity {
    const { id, userId, body, type } = dump;

    const createdAt = Timestamp.fromISOString(dump.createdAt);
    const updatedAt = Timestamp.fromISOString(dump.updatedAt);

    return new this(id, userId, body, createdAt, updatedAt, type);
  }

  isEqual(other: ModelCommentEntity): boolean {
    return isEqual(this.dump(), other.dump());
  }

  dump(): ModelCommentJSON {
    const { id, userId, body, type } = this;
    return {
      id,
      userId,
      body,
      type,
      createdAt: this.createdAt.toISOString(),
      updatedAt: this.updatedAt.toISOString(),
    };
  }

  cloneNew(): ModelCommentEntity {
    return new ModelCommentEntity(
      ModelCommentEntity.buildNewId(),
      this.userId,
      this.body,
      this.createdAt,
      this.updatedAt,
      this.type
    );
  }

  compareByPostAt(other: ModelCommentEntity): number {
    return this.getPostAt().compareTo(other.getPostAt());
  }

  getPostAt(): Timestamp {
    return this.createdAt;
  }

  getBody(): string {
    return this.body;
  }

  isRegularComment(): boolean {
    return this.type === 'Regular';
  }

  isSystemComment(): boolean {
    return this.type === 'System';
  }

  withBody(body: string): ModelCommentEntity {
    return new ModelCommentEntity(this.id, this.userId, body, this.createdAt, Timestamp.now(), this.type);
  }
}
